import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import Pagination from '../../../../components/forms/Pagination';
import { Section } from '../../../../components/ui/Section';
import {
  ScopedReferenceMarket,
  MarketItem,
  MarketItemUom,
  client,
} from '../../../../app/services/api/orderManagementClient';
import useLogError from '../../../../hooks/useLogError';

type Props = {
  companyId: number;
  customerTypeId?: number;
  contentId?: number;
  marketItemNumber: string;
  itemNumber?: string;
  markets: ScopedReferenceMarket[];
  currentUoms: MarketItemUom[];
};

type LinkedItem = {
  item: MarketItem;
  marketName: string;
};

type ItemGroup = {
  itemNumber: string;
  entries: LinkedItem[];
};

const getOrderableUomPrice = (item: MarketItem): number | undefined => (
  item.uoms.find((uom) => uom.isOrderableUom)?.price
);

const LINKED_ITEMS_PAGE_SIZE = 10;
const UOM_GROUPS_PAGE_SIZE = 5;

const LinkedItems: FC<Props> = ({
  companyId,
  customerTypeId,
  contentId,
  marketItemNumber,
  itemNumber,
  markets,
  currentUoms,
}) => {
  const { t } = useTranslation('productManagement');
  const { logError } = useLogError();
  const navigate = useNavigate();

  const [linkedItems, setLinkedItems] = useState<LinkedItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [linkedItemsPage, setLinkedItemsPage] = useState<number>(0);
  const [uomGroupsPage, setUomGroupsPage] = useState<number>(0);
  // Guards against out-of-order responses: a fast keystroke can fire a fetch that resolves after
  // a later one, which would otherwise overwrite the newer results with stale ones.
  const latestRequestId = useRef(0);

  const normalizedSku = marketItemNumber?.trim().toLowerCase() || '';
  const normalizedItemNumber = itemNumber?.trim().toLowerCase() || '';
  // Same 'marketitems' endpoint that backs the main product search box: searchTerm matches
  // against ProductCode (item SKU), InventorySKU (market SKU), and a few other fields.
  const searchTerms = Array.from(new Set([marketItemNumber?.trim(), itemNumber?.trim()].filter(Boolean))) as string[];
  // Restrict the search to the product's own market - inventory linkage should never cross markets.
  const sameMarketMarkets = markets.filter((market) => market.customerTypeId === customerTypeId);

  useEffect(() => {
    const loadLinkedItems = async () => {
      if (searchTerms.length === 0 || !companyId || sameMarketMarkets.length === 0) {
        setLinkedItems([]);
        return;
      }

      const requestId = ++latestRequestId.current;
      setIsLoading(true);
      try {
        const responses = await Promise.all(
          sameMarketMarkets.flatMap((market) => searchTerms.map((term) => client.getMarketItems({
            companyId: companyId.toString(),
            customerTypeId: market.customerTypeId.toString(),
            searchTerm: term,
            pageNumber: 0,
            pageSize: 2147483647,
          }).then((response) => ({ market, response })).catch(() => ({ market, response: undefined })))),
        );

        // A newer search has since started - discard this now-stale response.
        if (requestId !== latestRequestId.current) return;

        const matchesByKey = new Map<string, LinkedItem>();
        responses.forEach(({ market, response }) => {
          if (!response?.success || !response.data) return;
          (response.data.items || []).forEach((item) => {
            const isCurrentRecord = item.contentId === contentId && market.customerTypeId === customerTypeId;
            if (isCurrentRecord) return;

            const matchesSku = normalizedSku && (item.marketItemNumber || '').trim().toLowerCase() === normalizedSku;
            const matchesItemNumber = normalizedItemNumber && (item.itemNumber || '').trim().toLowerCase() === normalizedItemNumber;
            if (!matchesSku && !matchesItemNumber) return;

            const key = `${item.contentId}-${market.customerTypeId}`;
            if (!matchesByKey.has(key)) {
              matchesByKey.set(key, { item, marketName: market.name });
            }
          });
        });

        setLinkedItems(Array.from(matchesByKey.values()));
        setLinkedItemsPage(0);
        setUomGroupsPage(0);
      } catch (error) {
        if (requestId !== latestRequestId.current) return;
        logError('Failed to load linked items');
      } finally {
        if (requestId === latestRequestId.current) {
          setIsLoading(false);
        }
      }
    };

    // Debounced so each keystroke in the item/SKU fields doesn't fire its own search - only the
    // value left once typing pauses is searched, and requestId above still guards against any
    // response that arrives out of order regardless.
    const timeoutId = setTimeout(loadLinkedItems, 400);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedSku, normalizedItemNumber, companyId, contentId, customerTypeId, markets]);

  const handleViewItem = (item: MarketItem) => {
    const params = new URLSearchParams();
    params.set('companyId', item.companyId.toString());
    if (item.customerTypeId !== undefined) {
      params.set('customerTypeId', item.customerTypeId.toString());
    }
    navigate(`/procurement/products/edit/${item.contentId}?${params.toString()}`);
  };

  if (searchTerms.length === 0) {
    return null;
  }

  // Group linked items by item number so the comparison matrix can show one header per item,
  // with a column for each market variant of that item underneath it.
  const itemGroups: ItemGroup[] = Array.from(
    linkedItems
      .reduce((groups, entry) => {
        const key = entry.item.itemNumber || '-';
        const existing = groups.get(key);
        if (existing) {
          existing.push(entry);
        } else {
          groups.set(key, [entry]);
        }
        return groups;
      }, new Map<string, LinkedItem[]>())
      .entries(),
  )
    .map(([groupItemNumber, entries]) => ({ itemNumber: groupItemNumber, entries }))
    .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber));

  const linkedItemsMaxPages = Math.ceil(linkedItems.length / LINKED_ITEMS_PAGE_SIZE);
  const pagedLinkedItems = linkedItems.slice(
    linkedItemsPage * LINKED_ITEMS_PAGE_SIZE,
    (linkedItemsPage + 1) * LINKED_ITEMS_PAGE_SIZE,
  );

  const uomGroupsMaxPages = Math.ceil(itemGroups.length / UOM_GROUPS_PAGE_SIZE);
  const pagedItemGroups = itemGroups.slice(
    uomGroupsPage * UOM_GROUPS_PAGE_SIZE,
    (uomGroupsPage + 1) * UOM_GROUPS_PAGE_SIZE,
  );

  const getUomRowLabel = (uom: MarketItemUom): string => {
    const baseName = uom.uomTypeName || uom.uomName || t('uomType');
    if (uom.isPrimaryUom) return `${baseName} (${t('primaryUom')})`;
    if (uom.isOrderableUom) return `${baseName} (${t('orderableUom')})`;
    return baseName;
  };

  const findMatchingUom = (item: MarketItem, rowUom: MarketItemUom): MarketItemUom | undefined => (
    item.uoms.find((uom) => uom.uomTypeCode
      ? uom.uomTypeCode === rowUom.uomTypeCode
      : uom.uomTypeName === rowUom.uomTypeName)
  );

  const isMismatch = (rowUom: MarketItemUom, matchedUom?: MarketItemUom): boolean => {
    if (!matchedUom) return true;
    return matchedUom.uomName !== rowUom.uomName
      || matchedUom.numberOfBaseUnitsOrEaches !== rowUom.numberOfBaseUnitsOrEaches;
  };

  const getUomDisplay = (uom: MarketItemUom): string => `${uom.uomName} (${uom.numberOfBaseUnitsOrEaches})`;

  return (
    <Box sx={{ mt: 6 }}>
      {isLoading ? (
        <LoadingWheel />
      ) : linkedItems.length === 0 ? (
        <>
          <Typography
            variant="textMD"
            fontWeight={600}
            sx={{ mb: 2 }}
          >
            {t('linkedItems', 'Linked Items')}
          </Typography>
          <Typography
            variant="textSM"
            color="text.secondary"
          >
            {t('noLinkedItems', 'No other products share this Item or Inventory/Market SKU.')}
          </Typography>
        </>
      ) : (
        <>
          <Section>
            <Typography
              variant="textMD"
              fontWeight={600}
              component="div"
              sx={{ p: 4 }}
            >
              {t('linkedItems', 'Linked Items')}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('itemNumber')}</TableCell>
                    <TableCell>{t('itemName')}</TableCell>
                    <TableCell>{t('category')}</TableCell>
                    <TableCell>{t('primaryUom')}</TableCell>
                    <TableCell>{t('orderableUom')}</TableCell>
                    <TableCell align="right">{t('price')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedLinkedItems.map(({ item }) => {
                    const price = getOrderableUomPrice(item);
                    return (
                      <TableRow key={`${item.contentId}-${item.customerTypeId}`}>
                        <TableCell>
                          <Typography
                            component="a"
                            variant="textSM"
                            onClick={() => handleViewItem(item)}
                            sx={{
                              color: 'primary.main',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            {item.itemNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.itemName}</TableCell>
                        <TableCell>{item.primaryCategoryName || item.marketPrimaryCategoryName || '-'}</TableCell>
                        <TableCell>{item.primaryUom || '-'}</TableCell>
                        <TableCell>{item.orderableUom || '-'}</TableCell>
                        <TableCell align="right">{price ? price.toFixed(2) : '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              page={linkedItemsPage}
              setPage={setLinkedItemsPage}
              maxPages={linkedItemsMaxPages}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </Section>

          {currentUoms.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Section>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                  component="div"
                  sx={{ p: 4 }}
                >
                  {t('uomComparison', 'Unit of Measure Comparison')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('uomType')}</TableCell>
                        {pagedItemGroups.flatMap((group) => group.entries.map((entry) => (
                          <TableCell
                            key={`${entry.item.contentId}-${entry.item.customerTypeId}`}
                            align="center"
                            onClick={() => handleViewItem(entry.item)}
                            sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                          >
                            {`${group.itemNumber} - ${entry.item.itemName}`}
                          </TableCell>
                        )))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentUoms.map((rowUom, rowIndex) => (
                        <TableRow key={rowIndex}>
                          <TableCell variant="head">{getUomRowLabel(rowUom)}</TableCell>
                          {pagedItemGroups.flatMap((group) => group.entries.map((entry) => {
                            const matchedUom = findMatchingUom(entry.item, rowUom);
                            const mismatch = isMismatch(rowUom, matchedUom);
                            return (
                              <TableCell
                                key={`${entry.item.contentId}-${entry.item.customerTypeId}`}
                                align="center"
                                sx={mismatch ? { bgcolor: 'rgba(211, 47, 47, 0.12)', color: 'error.dark', fontWeight: 600 } : undefined}
                              >
                                {mismatch
                                  ? `${getUomDisplay(rowUom)} vs ${matchedUom ? getUomDisplay(matchedUom) : t('noMatch', 'No match')}`
                                  : (matchedUom ? getUomDisplay(matchedUom) : '-')}
                              </TableCell>
                            );
                          }))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Pagination
                  page={uomGroupsPage}
                  setPage={setUomGroupsPage}
                  maxPages={uomGroupsMaxPages}
                  breakpointForChangeDisplay={120000}
                  useNoDataMessage={false}
                />
              </Section>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default LinkedItems;
